import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { trpc } from '../utils/trpc';
import styles from '../styles/CreateEvent.module.css';
import TagManagerModal from '../page-components/TagManagerModal.tsx';

//Shape of a tag object, used for managing event tags in the TagManagerModal
interface Tag {
  id: number;
  name: string;
  category_id?: number;
}

//Shape of a venue suggestion object, used for managing venue selection in the autocomplete input
interface VenueSuggestion {
  venue_id: string;
  venue_name: string;
  address: string | null;
  location: string | null;
  open_time: string | null;
  close_time: string | null;
  max_capacity: number | null;
  banner_url: string | null;
  desc: string | null;
}

//Main component for creating a new event within a group, including form inputs for event details and venue selection
export default function CreateEventPage() {
  //Get groupId from URL parameters
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();

  const [eventName, setEventName] = useState('');
  const [date, setDate] = useState('');
  const [description, setDescription] = useState('');
  const [maxCapacity, setMaxCapacity] = useState<number | ''>('');
  const [duration_minutes, setDuration] = useState<number | ''>('');
  const [venueQuery, setVenueQuery] = useState('');
  const [selectedVenue, setSelectedVenue] = useState<VenueSuggestion | null>(null);
  const [showVenueSuggestions, setShowVenueSuggestions] = useState(false);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [errorText, setErrorText] = useState('');
  const [isSelectTagModalOpen, setIsSelectTagModalOpen] = useState(false);

  const utils = trpc.useUtils();

  //Fetch venue suggestions based on the user's input in the venue search field, with a minimum of 2 characters required to trigger the search
  const { data: venueSuggestions = [], isLoading: isVenueLoading } =
    trpc.venue.searchByName.useQuery(
      { query: venueQuery.trim(), limit: 8 },
      {
        enabled: venueQuery.trim().length >= 2,
      },
    );

  //Mutation for creating a new event, with success and error handlers
  const createMutation = trpc.event.createEvent.useMutation({
    onSuccess: () => {
      if (!groupId) return;

      utils.event.getEventsByGroupId.invalidate({ groupId, upcomingOnly: true });
      utils.event.getEventsByGroupId.invalidate({ groupId, upcomingOnly: false });

      navigate(`/groups/${groupId}/events`);
    },
    onError: (err: unknown) => {
      console.error('Error creating event:', err);
      setErrorText('Failed to create event. Please try again.');
    },
  });

  //Handler for form submission (user clicks save button), validates inputs and calls the create event mutation with the form data
  const handleSave = async () => {
    setErrorText('');

    if (!eventName || !date) {
      setErrorText('Event name and date/time are required.');
      return;
    }

    if (new Date(date) < new Date()) {
      setErrorText('Cannot create an event in the past.');
      return;
    }

    if (!selectedVenue) {
      setErrorText('Please select a venue from the suggestions.');
      return;
    }

    if (!groupId) {
      setErrorText('Missing group ID.');
      return;
    }

    try {
      const datetimeString = new Date(date).toISOString();

      const capacity = typeof maxCapacity === 'number' ? maxCapacity : undefined;

      await createMutation.mutateAsync({
        groupId,
        eventName,
        date: datetimeString,
        venueId: selectedVenue.venue_id,
        description: description || undefined,
        maxCapacity: capacity,
        duration_minutes:
          typeof duration_minutes === 'number' ? duration_minutes : undefined,
        tags: selectedTags.map((tag) => tag.name),
      });
    } catch (e) {
      console.error('Error creating event:', e);
      setErrorText('Failed to create event. Please try again.');
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.formContainer}>
        <h1>Create Event</h1>

        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          {/* Event Name */}
          <div className={styles.fieldContainer}>
            <label>Event Name *</label>
            <input
              className={`${styles.field} ${
                !eventName && errorText ? styles.invalid : ''
              }`}
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
            />
          </div>

          <div className={styles.fieldContainer}>
            <label>Venue *</label>
            <div className={styles.autocompleteWrapper}>
              <input
                className={`${styles.field} ${
                  !selectedVenue && errorText ? styles.invalid : ''
                }`}
                placeholder="Search venue by name"
                value={venueQuery}
                onFocus={() => setShowVenueSuggestions(true)}
                onBlur={() => {
                  setTimeout(() => setShowVenueSuggestions(false), 150);
                }}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  setVenueQuery(nextValue);
                  setShowVenueSuggestions(true);

                  if (
                    selectedVenue &&
                    nextValue.trim().toLowerCase() !==
                      selectedVenue.venue_name.toLowerCase()
                  ) {
                    setSelectedVenue(null);
                  }
                }}
              />

              {showVenueSuggestions && venueQuery.trim().length >= 2 && (
                <div className={styles.suggestionList}>
                  {isVenueLoading && (
                    <div className={styles.suggestionItem}>Searching venues...</div>
                  )}

                  {!isVenueLoading && venueSuggestions.length === 0 && (
                    <div className={styles.suggestionItem}>No venues found</div>
                  )}

                  {!isVenueLoading &&
                    venueSuggestions.map((venue) => (
                      <button
                        type="button"
                        key={venue.venue_id}
                        className={styles.suggestionButton}
                        onClick={() => {
                          setSelectedVenue(venue);
                          setVenueQuery(venue.venue_name);
                          setShowVenueSuggestions(false);
                        }}
                      >
                        <span className={styles.suggestionName}>{venue.venue_name}</span>
                        <span className={styles.suggestionMeta}>
                          {venue.address || 'Address not listed'}
                        </span>
                      </button>
                    ))}
                </div>
              )}
            </div>

            {selectedVenue ? (
              <div className={styles.venueCard}>
                <p className={styles.venueTitle}>{selectedVenue.venue_name}</p>
                <p>{selectedVenue.address || 'Address not listed'}</p>
                {selectedVenue.max_capacity != null && (
                  <p>Venue capacity: {selectedVenue.max_capacity}</p>
                )}
                {(selectedVenue.open_time || selectedVenue.close_time) && (
                  <p>
                    Hours: {selectedVenue.open_time || '?'} -{' '}
                    {selectedVenue.close_time || '?'}
                  </p>
                )}
              </div>
            ) : (
              <p className={styles.helperText}>
                Start typing at least 2 letters, then choose a venue from the list.
              </p>
            )}
          </div>

          {/* Description */}
          <div className={styles.fieldContainer}>
            <label>Description</label>
            <input
              type="text"
              className={styles.field}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* Date + Time */}
          <div className={styles.fieldContainer}>
            <label>Date *</label>
            <input
              type="datetime-local"
              className={styles.field}
              min={new Date().toISOString().slice(0, 16)}
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>

          {/* Duration */}
          <div className={styles.fieldContainer}>
            <label htmlFor="duration">Duration (minutes)</label>
            <input
              type="number"
              id="duration"
              min="1"
              className={styles.field}
              value={duration_minutes}
              onChange={(e) =>
                setDuration(e.target.value ? parseInt(e.target.value, 10) : '')
              }
            />
          </div>

          {/* Capacity */}
          <div className={styles.fieldContainer}>
            <label htmlFor="capacity">Max Capacity</label>
            <input
              type="number"
              id="capacity"
              min="1"
              className={styles.field}
              value={maxCapacity}
              onChange={(e) =>
                setMaxCapacity(e.target.value ? parseInt(e.target.value, 10) : '')
              }
            />
          </div>

          {/* Tags */}
          <div className={styles.fieldContainer}>
            <label>Interests</label>

            <button
              type="button"
              className={styles.button}
              onClick={() => setIsSelectTagModalOpen(true)}
            >
              Select Interests
            </button>

            {selectedTags.length > 0 && (
              <div className={styles.tagPreview}>
                {selectedTags.map((tag) => (
                  <span key={tag.id} className={styles.tag}>
                    {tag.name}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Error */}
          <div className={`${styles.reminder} ${errorText ? '' : styles.hidden}`}>
            {errorText || 'placeholder'}
          </div>

          {/* Buttons */}
          <div className={styles.buttonContainer}>
            <button
              type="button"
              className={`${styles.button} ${styles.cancelButton}`}
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>

            <button
              type="submit"
              className={`${styles.button} ${styles.submitButton}`}
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? 'Creating...' : 'Create Event'}
            </button>
          </div>
        </form>
      </div>

      {/* Tag selection modal */}
      <TagManagerModal
        isOpen={isSelectTagModalOpen}
        onClose={() => setIsSelectTagModalOpen(false)}
        mode="select"
        profileType="event"
        initialTags={selectedTags}
        min={1}
        max={10}
        onSave={(tags) => {
          setSelectedTags(tags);
          setIsSelectTagModalOpen(false);
        }}
      />
    </div>
  );
}
